import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Fab,
  FormControlLabel,
  GlobalStyles,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  ErrorOutline,
  FilterAlt,
  Functions,
  History,
  LockOpen,
  Print,
  RestartAlt,
  Undo,
  Verified,
  WarningAmber,
} from "@mui/icons-material";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import { fetchAllUsers } from "@/api/allUsers";
import type { User } from "@/api/user.service";
import { ConfirmModal } from "@/components/ConfirmModal";
import { InfoField } from "@/components/InfoField";
import { EscalaLogModal } from "@/components/escala/EscalaLogModal";
import { EscalaCargaDiaModal, type EscolhaCargaDia } from "@/components/escala/EscalaCargaDiaModal";
import { EscalaAvisosModal } from "@/components/escala/EscalaAvisosModal";
import { EscalaOcorrenciasModal } from "@/components/escala/EscalaOcorrenciasModal";
import { EspelhoGrade } from "@/components/escala/EspelhoGrade";
import { EspelhoImpressao } from "@/components/escala/EspelhoImpressao";
import { EscalinhaImpressao } from "@/components/escala/EscalinhaImpressao";
import { LegendaDoEspelho } from "@/components/escala/EspelhoLegenda";
import { HistoricoDaGerada } from "@/components/escala/HistoricoDaGerada";
import { StatusGeradaChip } from "@/components/escala/StatusGeradaChip";
import { botaoFlutuante } from "@/components/escala/espelhoEstilos";
import { infracoesDasOcorrencias, pendenciasPorColaborador, proximoTipo, quantidadeDeAvisos } from "@/components/escala/espelho";
import {
  alterarCargaDia,
  alterarDia,
  aprovarGerada,
  formatDataBR,
  formatTimestampBR,
  obterEspelho,
  resetarGerada,
  validarGerada,
  rotuloCompetencia,
  type Aviso,
  type Espelho,
  type EspelhoColaborador,
  type EspelhoDia,
  type Ocorrencia,
  type TipoDia,
} from "@/api/escala.service";

type Transicao = "validar" | "desvalidar" | "aprovar" | "desaprovar";

// Botões do cabeçalho: todos com ícone, na mesma altura e com a mesma largura mínima. O padding do
// tema (8px 20px em todo tamanho) deixava o outlined 2px mais alto que o contained, pela borda.
const BOTAO = { height: 34, minWidth: 112, px: 1.5, py: 0, whiteSpace: "nowrap" } as const;

const CONFIRMACAO: Record<Transicao, { title: string; text: string; btnText: string; btnColor: "primary" | "error" }> = {
  validar: {
    title: "Validar escala",
    text: "Validada, a escala fica travada para edição até voltar a rascunho. Validar?",
    btnText: "Validar",
    btnColor: "primary",
  },
  desvalidar: {
    title: "Voltar a rascunho",
    text: "A escala volta a rascunho e pode ser editada de novo. Voltar a rascunho?",
    btnText: "Voltar a rascunho",
    btnColor: "error",
  },
  aprovar: {
    title: "Aprovar escala",
    text: "Aprovada, a escala vira a fonte dos relatórios e a liberação fora do prazo é encerrada. Aprovar?",
    btnText: "Aprovar",
    btnColor: "primary",
  },
  desaprovar: {
    title: "Desaprovar escala",
    text: "A escala volta a rascunho e sai dos relatórios até ser validada e aprovada de novo. Desaprovar?",
    btnText: "Desaprovar",
    btnColor: "error",
  },
};

// "Escala X - Setembro-2026": sem os caracteres que o sistema de arquivos não aceita.
function nomeDoArquivo(gerada: Espelho["gerada"], prefixo?: string) {
  const nome = `${gerada.escala_nome} - ${rotuloCompetencia(gerada.ano, gerada.mes)}`;
  return (prefixo ? `${prefixo} - ${nome}` : nome).replace(/[\\/:*?"<>|]+/g, "-");
}

// O que está sendo impresso: a escala ou as escalinhas, coloridas ou em preto e branco; as escalinhas
// de todos ou de um colaborador. Com e sem carga horária, as mesmas opções.
interface Impressao {
  // O Chrome usa o título da página como nome sugerido ao salvar em PDF.
  arquivo: string;
  modo: "escala" | "escalinha";
  pretoBranco?: boolean;
  colaboradorId?: string;
  // "15/10/2026 14:30", no cabeçalho de cada página da escala.
  impressoEm: string;
}

function novaImpressao(
  gerada: Espelho["gerada"],
  modo: Impressao["modo"],
  opcoes: { pretoBranco?: boolean; colaborador?: EspelhoColaborador } = {},
): Impressao {
  const { pretoBranco, colaborador } = opcoes;
  const prefixo = modo === "escalinha" ? (colaborador ? `Escalinha ${colaborador.nome}` : "Escalinhas") : undefined;
  const agora = new Date();
  return {
    arquivo: nomeDoArquivo(gerada, prefixo),
    modo,
    pretoBranco,
    colaboradorId: colaborador?.id,
    impressoEm: `${agora.toLocaleDateString("pt-BR")} ${agora.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`,
  };
}

// Margem 0: com qualquer margem em cima ou embaixo, o Chrome põe nela o cabeçalho e o rodapé dele
// (título, data e URL). A margem visual fica dentro de cada página da escala e da escalinha.
const PAGINA_DE_IMPRESSAO = { size: "A4 landscape", margin: 0 };

function nomeDoUsuario(usuarios: User[], id?: string) {
  return usuarios.find((u) => u.id === id)?.name ?? "";
}

// "— nome" só quando o nome já resolveu (usuarios ainda carregando, ou o autor não existe mais) —
// sem isso a data ficava com um traço solto no fim (R51).
function rotuloComQuem(data: string, quemId: string | undefined, usuarios: User[]) {
  const nome = nomeDoUsuario(usuarios, quemId);
  return nome ? `${formatTimestampBR(data)} — ${nome}` : formatTimestampBR(data);
}

const TEXTO_DO_ACEITE = "Estou ciente dos avisos e assumo a responsabilidade pela escala com eles";

const TRANSICAO_CONCLUIDA: Record<Transicao, string> = {
  validar: "Escala validada",
  desvalidar: "Escala voltou a rascunho",
  aprovar: "Escala aprovada",
  desaprovar: "Escala desaprovada e de volta a rascunho",
};

interface EscalaEspelhoViewProps {
  geradaId: string;
  // Chamado quando uma transição de estado (validar, aprovar...) é gravada, pra quem lista as geradas atualizar.
  onEstadoAlterado?: () => void;
  // Dispara a impressão assim que o espelho carregar — atalho da linha na lista de geradas, sem
  // abrir a tela do espelho: quem usa isso monta o componente fora da tela (ver EscalaGeradasTab).
  autoImprimir?: boolean;
  // Com autoImprimir, imprime só a escalinha deste colaborador, em vez da escala.
  autoImprimirColaboradorId?: string;
  // Chamado depois que a impressão dispara (ou falha em disparar) — só faz sentido com
  // autoImprimir, pra quem montou o componente escondido saber a hora de desmontar.
  onImpressaoConcluida?: () => void;
}

export function EscalaEspelhoView({
  geradaId,
  onEstadoAlterado,
  autoImprimir,
  autoImprimirColaboradorId,
  onImpressaoConcluida,
}: EscalaEspelhoViewProps) {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  // Sufixo do id da área de impressão: sem ele, duas instâncias montadas ao mesmo tempo (dialog
  // do espelho + atalho de impressão da linha, ver EscalaGeradasTab) portariam pro body com o
  // mesmo id, e a folha de estilo de impressão poderia casar com as duas — imprimindo a grade
  // errada. useId() garante um valor único por instância mesmo em SSR.
  const printAreaId = `escala-print-area-${useId()}`;

  const [espelho, setEspelho] = useState<Espelho | null>(null);
  const [erro, setErro] = useState(false);
  // Validada_por/aprovada_por vêm como UUID cru do backend (04-contratos.md) — resolve o nome
  // pra exibir, mesmo padrão de EscalaPermissoesTab/EscalaGeradasTab (R51).
  const [usuarios, setUsuarios] = useState<User[]>([]);
  // A impressão automática espera os nomes, que vão no cabeçalho; se a busca falhar, sai só a data.
  const [usuariosProntos, setUsuariosProntos] = useState(false);
  useEffect(() => {
    fetchAllUsers()
      .then(setUsuarios)
      .catch(() => {})
      .finally(() => setUsuariosProntos(true));
  }, []);
  // Vêm com o espelho do rascunho e depois da resposta de cada edição, reset ou validação.
  const [ocorrencias, setOcorrencias] = useState<Ocorrencia[]>([]);
  // Mesmo caminho das ocorrências, mas não barram a validação.
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  // Só o que explica um "?": a edição de uma célula troca os dias, mas não estes, e a grade não
  // redesenha por causa deles.
  const colaboradoresDoEspelho = espelho?.colaboradores;
  const cargasDoEspelho = espelho?.cargas;
  const feriadosDoEspelho = espelho?.feriados;
  const espelhoEditavel = espelho?.editavel;
  const contextoSemHorario = useMemo(
    () =>
      colaboradoresDoEspelho && feriadosDoEspelho
        ? { colaboradores: colaboradoresDoEspelho, cargas: cargasDoEspelho, feriados: feriadosDoEspelho, editavel: espelhoEditavel }
        : undefined,
    [colaboradoresDoEspelho, cargasDoEspelho, feriadosDoEspelho, espelhoEditavel],
  );
  const infracoes = useMemo(() => infracoesDasOcorrencias(ocorrencias, contextoSemHorario), [ocorrencias, contextoSemHorario]);
  const pendencias = useMemo(() => pendenciasPorColaborador(ocorrencias, avisos), [ocorrencias, avisos]);
  // Na legenda, as cargas da escala e as que aparecem nos dias do mês, pelo nome.
  const cargasDaLegenda = useMemo(() => {
    if (!espelho) return [];
    const usadas = new Set(espelho.dias.map((d) => d.carga_horaria_id));
    return (espelho.cargas ?? [])
      .filter((c) => c.da_escala || usadas.has(c.id))
      .sort((a, b) => a.descricao.localeCompare(b.descricao, "pt-BR"));
  }, [espelho]);
  const [confirmandoReset, setConfirmandoReset] = useState(false);
  const [resetando, setResetando] = useState(false);
  const [confirmando, setConfirmando] = useState<Transicao | null>(null);
  // A caixa de aceite dos avisos na confirmação de validar e de aprovar; começa desmarcada a cada abertura.
  const [aceite, setAceite] = useState(false);
  const confirmar = (transicao: Transicao) => {
    setAceite(false);
    setConfirmando(transicao);
  };
  const [transicionando, setTransicionando] = useState(false);
  const [verOcorrencias, setVerOcorrencias] = useState(false);
  const [verAvisos, setVerAvisos] = useState(false);
  const [soDomingos, setSoDomingos] = useState(false);
  const [mostrarRodape, setMostrarRodape] = useState(true);
  const [verHistorico, setVerHistorico] = useState(false);
  // Log de um colaborador só, aberto pelo botão da linha.
  const [historicoDe, setHistoricoDe] = useState<EspelhoColaborador | null>(null);
  // Dia trabalhado cuja carga está sendo trocada.
  const [diaCarga, setDiaCarga] = useState<{ colaboradorId: string; data: string } | null>(null);
  // Só existe no DOM durante a impressão (evita duplicar o conteúdo o tempo todo —
  // inclusive pra query de teste, que não distingue "display: none").
  const [impressao, setImpressao] = useState<Impressao | null>(null);
  const imprimindo = impressao !== null;
  // Menu de impressão, com ou sem carga horária: colorida, preto e branco ou escalinhas.
  const [menuImpressao, setMenuImpressao] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (impressao === null) return;
    const tituloAnterior = document.title;
    document.title = impressao.arquivo;
    const aoTerminar = () => {
      setImpressao(null);
      onImpressaoConcluida?.();
    };
    window.addEventListener("afterprint", aoTerminar);
    window.print();
    return () => {
      document.title = tituloAnterior;
      window.removeEventListener("afterprint", aoTerminar);
    };
  }, [impressao, onImpressaoConcluida]);
  // Só imprime automaticamente uma vez, mesmo que o espelho recarregue depois (validar/aprovar).
  // Fora de APROVADA (ou se o espelho falhar em carregar) não há o que imprimir — avisa quem
  // montou o componente escondido pra ele desmontar, senão fica esperando pra sempre.
  const autoImprimiuRef = useRef(false);
  useEffect(() => {
    if (!autoImprimir || autoImprimiuRef.current) return;
    if (erro) {
      autoImprimiuRef.current = true;
      notify("Não foi possível carregar o espelho para imprimir", "error");
      onImpressaoConcluida?.();
      return;
    }
    if (!espelho) return;
    if (espelho.gerada.status !== "APROVADA") {
      autoImprimiuRef.current = true;
      notify("Só é possível imprimir uma escala aprovada", "error");
      onImpressaoConcluida?.();
      return;
    }
    if (!usuariosProntos) return;
    const colaborador = autoImprimirColaboradorId ? espelho.colaboradores.find((c) => c.id === autoImprimirColaboradorId) : undefined;
    if (autoImprimirColaboradorId && !colaborador) {
      autoImprimiuRef.current = true;
      notify("Colaborador não encontrado na escala aprovada", "error");
      onImpressaoConcluida?.();
      return;
    }
    autoImprimiuRef.current = true;
    setImpressao(colaborador ? novaImpressao(espelho.gerada, "escalinha", { colaborador }) : novaImpressao(espelho.gerada, "escala"));
  }, [espelho, erro, usuariosProntos, autoImprimir, autoImprimirColaboradorId, onImpressaoConcluida, notify]);

  // Botão da linha do colaborador, na escala aprovada: a escalinha dele, colorida.
  const geradaAtual = espelho?.gerada;
  const imprimirEscalinhaDe = useCallback(
    (colaborador: EspelhoColaborador) => {
      if (geradaAtual) setImpressao(novaImpressao(geradaAtual, "escalinha", { colaborador }));
    },
    [geradaAtual],
  );

  // Edições esperando o servidor: enquanto houver, o reset não pode começar.
  const [emVoo, setEmVoo] = useState(0);
  // Muda ao trocar de gerada e ao resetar: resposta de edição de antes disso não mexe mais nas ocorrências.
  const operacao = useRef(0);
  // Sequência de envio das edições — não a de chegada da resposta (R47: duas edições
  // concorrentes podem responder fora de ordem; as ocorrências exibidas têm que ser sempre as da
  // edição enviada por último, nunca as da que respondeu por último).
  const proximaEdicao = useRef(0);
  const edicaoAplicada = useRef(0);

  useEffect(() => {
    let cancelado = false;
    operacao.current++;
    edicaoAplicada.current = 0;
    setEspelho(null);
    setErro(false);
    setOcorrencias([]);
    setAvisos([]);
    setVerOcorrencias(false);
    setVerAvisos(false);
    obterEspelho(geradaId, clientId)
      .then((resultado) => {
        if (cancelado) return;
        setEspelho(resultado);
        setOcorrencias(resultado.ocorrencias ?? []);
        setAvisos(resultado.avisos ?? []);
      })
      .catch(() => !cancelado && setErro(true));
    return () => {
      cancelado = true;
    };
  }, [geradaId, clientId]);

  // Célula com edição em voo: um segundo clique nela espera o servidor responder.
  const pendentes = useRef(new Set<string>());

  // Só aplica se o espelho carregado ainda é o da gerada que originou a edição.
  const trocarDia = useCallback(
    (idGerada: string, colaboradorId: string, data: string, troca: (d: EspelhoDia) => EspelhoDia) => {
      setEspelho((atual) =>
        atual?.gerada.id === idGerada
          ? {
              ...atual,
              dias: atual.dias.map((d) => (d.colaborador_id === colaboradorId && d.data === data ? troca(d) : d)),
            }
          : atual,
      );
    },
    [],
  );

  // Atualiza a célula na hora e desfaz se o servidor recusar.
  const ciclar = useCallback(
    (colaboradorId: string, data: string, atual: TipoDia) => {
      const novo = proximoTipo(atual);
      const chave = `${geradaId}|${colaboradorId}|${data}`;
      if (!novo || resetando || transicionando || pendentes.current.has(chave)) return;

      // Edições concorrentes não se invalidam: a validação de cada uma enxerga as anteriores,
      // então a última enviada é a mais completa — mesmo que a resposta dela chegue antes da de
      // uma edição enviada mais cedo (R47).
      const minha = operacao.current;
      const minhaEdicao = ++proximaEdicao.current;
      pendentes.current.add(chave);
      setEmVoo((n) => n + 1);
      // Até a resposta, muda só o tipo; a resposta traz o dia inteiro (marca de alterado inclusive).
      trocarDia(geradaId, colaboradorId, data, (d) => ({ ...d, tipo: novo }));
      alterarDia({ gerada_id: geradaId, colaborador_id: colaboradorId, data, tipo: novo }, clientId)
        .then((r) => {
          trocarDia(geradaId, colaboradorId, data, () => r.dia);
          if (minha === operacao.current && minhaEdicao > edicaoAplicada.current) {
            edicaoAplicada.current = minhaEdicao;
            setOcorrencias(r.ocorrencias);
            setAvisos(r.avisos ?? []);
          }
        })
        .catch((e) => {
          trocarDia(geradaId, colaboradorId, data, (d) => ({ ...d, tipo: atual }));
          notify(e?.message || "Erro ao alterar o dia", "error");
        })
        .finally(() => {
          pendentes.current.delete(chave);
          setEmVoo((n) => n - 1);
        });
    },
    [geradaId, clientId, notify, trocarDia, resetando, transicionando],
  );

  // A janela abre pelo botão direito; a célula com edição em voo espera a resposta.
  const abrirTrocaCarga = useCallback(
    (colaboradorId: string, data: string) => {
      if (resetando || transicionando || pendentes.current.has(`${geradaId}|${colaboradorId}|${data}`)) return;
      setDiaCarga({ colaboradorId, data });
    },
    [geradaId, resetando, transicionando],
  );

  // Mesmas travas do duplo clique: o reset espera, e a resposta de antes de um reset ou de outra
  // gerada não mexe mais na grade. Rejeita quando o servidor recusa, para a janela continuar aberta.
  const trocarCargaDia = async (escolha: EscolhaCargaDia) => {
    if (!diaCarga) return;
    const { colaboradorId, data } = diaCarga;
    const chave = `${geradaId}|${colaboradorId}|${data}`;
    if (pendentes.current.has(chave)) return;
    const minha = operacao.current;
    const minhaEdicao = ++proximaEdicao.current;
    pendentes.current.add(chave);
    setEmVoo((n) => n + 1);
    try {
      const r = await alterarCargaDia({ gerada_id: geradaId, colaborador_id: colaboradorId, data, ...escolha }, clientId);
      if (minha === operacao.current) {
        trocarDia(geradaId, colaboradorId, data, () => r.dia);
        if (minhaEdicao > edicaoAplicada.current) {
          edicaoAplicada.current = minhaEdicao;
          setOcorrencias(r.ocorrencias);
          setAvisos(r.avisos ?? []);
        }
      }
      setDiaCarga(null);
    } catch (e) {
      notify((e as Error)?.message || "Erro ao trocar a carga horária do dia", "error");
      throw e;
    } finally {
      pendentes.current.delete(chave);
      setEmVoo((n) => n - 1);
    }
  };

  // Descarta as edições manuais no servidor e recarrega a grade original.
  const resetar = () => {
    setConfirmandoReset(false);
    setResetando(true);
    const minha = ++operacao.current;
    edicaoAplicada.current = 0;
    resetarGerada(geradaId, clientId)
      .then(async (r) => {
        let original: Espelho;
        try {
          original = await obterEspelho(geradaId, clientId);
        } catch {
          // O reset já foi gravado: a grade na tela é que ficou velha.
          if (minha === operacao.current) notify("A escala foi resetada, mas não foi possível recarregar a grade. Reabra o espelho.", "error");
          return;
        }
        if (minha !== operacao.current) return;
        setEspelho(original);
        setOcorrencias(r.ocorrencias);
        setAvisos(r.avisos ?? []);
        if (r.dias_restaurados > 0) notify(`${r.dias_restaurados} dia(s) voltaram ao original`, "success");
        else notify("Não há edições manuais para descartar", "info");
      })
      .catch((e) => notify(e?.message || "Erro ao resetar a escala", "error"))
      .finally(() => setResetando(false));
  };

  // Muda o estado da gerada no servidor e recarrega o espelho: editavel e acoes dependem do novo status.
  const transitar = async (transicao: Transicao) => {
    setConfirmando(null);
    setTransicionando(true);
    const minha = ++operacao.current;
    // A lista que a confirmação mostrou e o Gerente aceitou; o servidor confere com a dele.
    const aceitos = avisos;
    // O servidor achou outra lista (alguém editou ou mudou o cadastro no meio): a confirmação reabre com ela.
    const aceitePendente = (novos: Aviso[] = []) => {
      if (minha !== operacao.current) return;
      setAvisos(novos);
      notify("Os avisos mudaram desde que a tela abriu. Confira a lista nova e confirme de novo.", "warning");
      confirmar(transicao);
    };
    try {
      if (transicao === "validar") {
        const r = await validarGerada(geradaId, true, clientId, aceitos);
        if (r.ocorrencias.length > 0) {
          // Validar não é carimbo: com ocorrência o estado não mudou; o modal detalha e a grade marca as células.
          if (minha === operacao.current) {
            setOcorrencias(r.ocorrencias);
            setVerOcorrencias(true);
          }
          return;
        }
        if (r.aceite_pendente) {
          aceitePendente(r.avisos);
          return;
        }
      } else if (transicao === "desvalidar") {
        await validarGerada(geradaId, false, clientId);
      } else if (transicao === "aprovar") {
        const r = await aprovarGerada(geradaId, true, clientId, aceitos);
        if (r.aceite_pendente) {
          aceitePendente(r.avisos);
          return;
        }
      } else {
        await aprovarGerada(geradaId, false, clientId);
      }

      onEstadoAlterado?.();

      let atualizado: Espelho;
      try {
        atualizado = await obterEspelho(geradaId, clientId);
      } catch {
        if (minha === operacao.current) notify("O estado da escala mudou, mas não foi possível recarregar a grade. Reabra o espelho.", "error");
        return;
      }
      if (minha !== operacao.current) return;
      setEspelho(atualizado);
      setOcorrencias(atualizado.ocorrencias ?? []);
      setAvisos(atualizado.avisos ?? []);
      setVerOcorrencias(false);
      notify(TRANSICAO_CONCLUIDA[transicao], "success");
    } catch (e) {
      notify((e as Error)?.message || "Erro ao alterar o estado da escala", "error");
      // A recusa pode vir de estado defasado (outra pessoa mudou antes) ou de falha depois do commit:
      // recarrega para os botões e as ocorrências refletirem o estado real.
      obterEspelho(geradaId, clientId)
        .then((atual) => {
          if (minha !== operacao.current) return;
          setEspelho(atual);
          setOcorrencias(atual.ocorrencias ?? []);
          setAvisos(atual.avisos ?? []);
        })
        .catch(() => {});
    } finally {
      setTransicionando(false);
    }
  };

  if (erro) return <Alert severity="error">Não foi possível carregar o espelho desta escala.</Alert>;
  if (!espelho) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress aria-label="Carregando espelho" />
      </Stack>
    );
  }

  const { gerada, feriados, colaboradores, acoes } = espelho;
  // A semana acima da jornada conta uma por pessoa, como o modal mostra.
  const totalAvisos = quantidadeDeAvisos(avisos, colaboradores);
  const ocupado = resetando || transicionando || emVoo > 0;
  const confirmacao = confirmando ? CONFIRMACAO[confirmando] : undefined;
  // Validar e aprovar com aviso dizem quantos são, mandam conferir a lista no modal de avisos e só
  // seguem com a caixa de aceite marcada.
  const pedeAceite = (confirmando === "validar" || confirmando === "aprovar") && avisos.length > 0;
  // Com aviso, a de validar fica só com ele; a de aprovar mantém o texto, que fala da liberação fora do prazo.
  const textoDosAvisos = `A escala tem ${totalAvisos} ${totalAvisos === 1 ? "aviso" : "avisos"}. Confira a lista antes de ${confirmando}.`;
  const textoDaConfirmacao =
    confirmacao && pedeAceite
      ? confirmando === "validar"
        ? textoDosAvisos
        : `${textoDosAvisos}\n\n${confirmacao.text}`
      : confirmacao?.text;
  const historicoAvisos = espelho.historico_avisos ?? [];
  // Na escala aprovada o servidor não recalcula os avisos: o botão fica para o histórico.
  const temHistoricoDeAvisos = historicoAvisos.some((r) => r.avisos.length > 0);

  const podeImprimir = gerada.status === "APROVADA";
  // Separa, no cabeçalho, os botões de consulta dos que mudam a escala.
  const mudaAEscala = espelho.editavel || acoes.desvalidar || acoes.desaprovar || acoes.validar || acoes.aprovar;

  return (
    <Stack spacing={2.5} sx={{ flex: 1, minHeight: 0, position: "relative" }}>
      <GlobalStyles
        styles={{
          "@media print": {
            // A área de impressão é portada pra filho direto do body (fora de toda a árvore do
            // app) — então basta esconder os outros filhos diretos do body, sem depender de
            // conhecer o shell do painel (sidebar, abas etc). `visibility` não serviria aqui:
            // continuaria reservando espaço e gerando página em branco.
            "body > *": { display: "none" },
            [`body > [id="${printAreaId}"]`]: { display: "block" },
            // Sem o fundo da página do app nas bordas da folha.
            body: { backgroundColor: "#fff" },
            "@page": PAGINA_DE_IMPRESSAO,
          },
        }}
      />
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        columnGap={3}
        rowGap={1.5}
        sx={{ px: 2.5, py: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 3, bgcolor: "background.paper" }}
      >
        {/* Todo texto do cabeçalho no mesmo formato rótulo/valor, e o status depois deles, centrado
            na altura dos dois. A base 0 faz este grupo quebrar por dentro antes de empurrar os
            botões pra linha de baixo. */}
        <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={4} rowGap={1.5} sx={{ flex: "1 1 0", minWidth: 320 }}>
          <InfoField destaque label="Escala" value={gerada.escala_nome} />
          <InfoField destaque label="Loja" value={gerada.loja_nome} />
          <InfoField destaque label="Competência" value={rotuloCompetencia(gerada.ano, gerada.mes)} />
          {gerada.validada_em && (
            <InfoField destaque label="Validada em" value={rotuloComQuem(gerada.validada_em, gerada.validada_por, usuarios)} />
          )}
          {gerada.aprovada_em && (
            <InfoField destaque label="Aprovada em" value={rotuloComQuem(gerada.aprovada_em, gerada.aprovada_por, usuarios)} />
          )}
          <StatusGeradaChip status={gerada.status} />
        </Stack>
        {/* Consulta à esquerda do traço; o que muda a escala, à direita. */}
        <Stack direction="row" flexWrap="wrap" alignItems="center" justifyContent="flex-end" columnGap={1} rowGap={1} sx={{ ml: "auto" }}>
          <Button
            variant={soDomingos ? "contained" : "outlined"}
            size="small"
            startIcon={<FilterAlt />}
            aria-pressed={soDomingos}
            sx={BOTAO}
            onClick={() => setSoDomingos((v) => !v)}
          >
            Domingos
          </Button>
          <Button
            variant={mostrarRodape ? "contained" : "outlined"}
            size="small"
            startIcon={<Functions />}
            aria-pressed={mostrarRodape}
            sx={BOTAO}
            onClick={() => setMostrarRodape((v) => !v)}
          >
            Totais
          </Button>
          {acoes.log && (
            <Button variant="outlined" size="small" startIcon={<History />} sx={BOTAO} onClick={() => setVerHistorico(true)}>
              Histórico
            </Button>
          )}
          {ocorrencias.length > 0 && (
            <Button variant="outlined" color="error" size="small" startIcon={<ErrorOutline />} sx={BOTAO} onClick={() => setVerOcorrencias(true)}>
              Ocorrências ({ocorrencias.length})
            </Button>
          )}
          {(avisos.length > 0 || temHistoricoDeAvisos) && (
            <Button variant="outlined" color="warning" size="small" startIcon={<WarningAmber />} sx={BOTAO} onClick={() => setVerAvisos(true)}>
              {avisos.length > 0 ? `Avisos (${totalAvisos})` : "Avisos"}
            </Button>
          )}
          {mudaAEscala && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
          {espelho.editavel && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RestartAlt />}
              sx={BOTAO}
              disabled={ocupado}
              onClick={() => setConfirmandoReset(true)}
            >
              Resetar
            </Button>
          )}
          {acoes.desvalidar && (
            <Button variant="outlined" size="small" startIcon={<Undo />} sx={BOTAO} disabled={ocupado} onClick={() => confirmar("desvalidar")}>
              Voltar a rascunho
            </Button>
          )}
          {acoes.desaprovar && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<LockOpen />}
              sx={BOTAO}
              disabled={ocupado}
              onClick={() => confirmar("desaprovar")}
            >
              Desaprovar
            </Button>
          )}
          {acoes.validar && (
            <Button variant="contained" size="small" startIcon={<CheckCircleOutline />} sx={BOTAO} disabled={ocupado} onClick={() => confirmar("validar")}>
              Validar
            </Button>
          )}
          {acoes.aprovar && (
            <Button variant="contained" size="small" startIcon={<Verified />} sx={BOTAO} disabled={ocupado} onClick={() => confirmar("aprovar")}>
              Aprovar
            </Button>
          )}
        </Stack>
      </Stack>

      {colaboradores.length === 0 ? (
        <Alert severity="info">Esta escala não tem colaboradores gerados.</Alert>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <EspelhoGrade
            espelho={espelho}
            onCiclar={ciclar}
            onTrocarCarga={abrirTrocaCarga}
            onVerLog={acoes.log ? setHistoricoDe : undefined}
            onImprimirEscalinha={gerada.status === "APROVADA" ? imprimirEscalinhaDe : undefined}
            infracoes={infracoes}
            pendencias={pendencias}
            soDomingos={soDomingos}
            mostrarRodape={mostrarRodape}
          />
        </Box>
      )}

      {feriados.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Feriados do mês
          </Typography>
          <Stack spacing={0.25}>
            {feriados.map((f) => (
              <Typography key={f.data} variant="body2" color="text.secondary">
                {formatDataBR(f.data)} — {f.nome}
                {f.fecha_loja ? " (loja fechada)" : ""}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {/* Imprimir, auditoria e legenda no canto inferior direito. Com feriados, ficam na faixa deles, à
          direita do texto; sem, caem sobre a célula vazia do rodapé abaixo de TT/TF — longe o
          bastante da borda para não cobrir as barras de rolagem da grade. */}
      <Stack direction="row" spacing={1} sx={{ position: "absolute", right: 20, bottom: 20, zIndex: 6 }}>
        <Tooltip describeChild placement="left" title={podeImprimir ? "Imprimir" : "Só é possível imprimir uma escala aprovada"}>
          {/* O span recebe o mouse quando o botão está desabilitado, para a dica aparecer. */}
          <Box component="span" sx={{ display: "flex" }}>
            <Fab
              size="small"
              aria-label="Imprimir"
              aria-haspopup="menu"
              disabled={imprimindo || !podeImprimir}
              sx={botaoFlutuante}
              onClick={(e) => setMenuImpressao(e.currentTarget)}
            >
              <Print fontSize="small" />
            </Fab>
          </Box>
        </Tooltip>
        <HistoricoDaGerada geradaId={geradaId} usuarios={usuarios} />
        <LegendaDoEspelho comCarga={!!gerada.com_carga_horaria} editavel={espelho.editavel} cargas={cargasDaLegenda} />
      </Stack>
      <Menu
        anchorEl={menuImpressao}
        open={!!menuImpressao}
        onClose={() => setMenuImpressao(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: -1 } } }}
      >
        {(
          [
            ["Escala colorida", "escala", false],
            ["Escala em preto e branco", "escala", true],
            ["Escalinhas coloridas", "escalinha", false],
            ["Escalinhas em preto e branco", "escalinha", true],
          ] as const
        ).map(([rotulo, modo, pretoBranco]) => (
          <MenuItem
            key={rotulo}
            onClick={() => {
              setMenuImpressao(null);
              setImpressao(novaImpressao(gerada, modo, { pretoBranco }));
            }}
          >
            {rotulo}
          </MenuItem>
        ))}
      </Menu>

      {impressao &&
        createPortal(
          <Box
            id={printAreaId}
            sx={{ display: "none", "@media print": { display: "block" } }}
          >
            {impressao.modo === "escalinha" ? (
              <EscalinhaImpressao espelho={espelho} colaboradorId={impressao.colaboradorId} pretoBranco={impressao.pretoBranco} />
            ) : (
              <EspelhoImpressao
                espelho={espelho}
                validadaEm={gerada.validada_em ? rotuloComQuem(gerada.validada_em, gerada.validada_por, usuarios) : undefined}
                aprovadaEm={gerada.aprovada_em ? rotuloComQuem(gerada.aprovada_em, gerada.aprovada_por, usuarios) : undefined}
                impressoEm={impressao.impressoEm}
                pretoBranco={impressao.pretoBranco}
              />
            )}
          </Box>,
          document.body,
        )}

      <ConfirmModal
        open={confirmandoReset}
        title="Resetar escala"
        text="Descartar todas as alterações manuais e voltar a escala ao estado em que foi gerada?"
        btnText="Resetar"
        onConfirm={resetar}
        onClose={() => setConfirmandoReset(false)}
      />
      {verHistorico && (
        <EscalaLogModal geradaId={geradaId} comCarga={!!gerada.com_carga_horaria} onClose={() => setVerHistorico(false)} />
      )}
      {historicoDe && (
        <EscalaLogModal
          geradaId={geradaId}
          colaborador={historicoDe}
          comCarga={!!gerada.com_carga_horaria}
          onClose={() => setHistoricoDe(null)}
        />
      )}
      <EscalaCargaDiaModal
        dia={diaCarga ? (espelho.dias.find((d) => d.colaborador_id === diaCarga.colaboradorId && d.data === diaCarga.data) ?? null) : null}
        colaborador={diaCarga ? colaboradores.find((c) => c.id === diaCarga.colaboradorId) : undefined}
        cargas={espelho.cargas ?? []}
        feriados={feriados}
        onClose={() => setDiaCarga(null)}
        onConfirmar={trocarCargaDia}
      />
      <EscalaOcorrenciasModal
        open={verOcorrencias}
        ocorrencias={ocorrencias}
        colaboradores={colaboradores}
        contexto={contextoSemHorario}
        onClose={() => setVerOcorrencias(false)}
      />
      <EscalaAvisosModal
        open={verAvisos}
        avisos={avisos}
        colaboradores={colaboradores}
        cargas={espelho.cargas}
        historico={historicoAvisos}
        usuarios={usuarios}
        onClose={() => setVerAvisos(false)}
      />
      {confirmacao && (
        <ConfirmModal
          open
          title={confirmacao.title}
          text={textoDaConfirmacao ?? confirmacao.text}
          btnText={confirmacao.btnText}
          btnColor={confirmacao.btnColor}
          confirmDisabled={pedeAceite && !aceite}
          onConfirm={() => confirmando && transitar(confirmando)}
          onClose={() => setConfirmando(null)}
        >
          {pedeAceite && (
            <Stack spacing={1.5} alignItems="flex-start" sx={{ mt: 2 }}>
              {/* O modal de avisos abre por cima da confirmação, e fechar volta a ela. */}
              <Button variant="outlined" color="warning" size="small" fullWidth startIcon={<WarningAmber />} onClick={() => setVerAvisos(true)}>
                Ver avisos
              </Button>
              <FormControlLabel
                sx={{ alignItems: "flex-start" }}
                control={<Checkbox checked={aceite} onChange={(e) => setAceite(e.target.checked)} sx={{ pt: 0.25 }} />}
                label={TEXTO_DO_ACEITE}
              />
            </Stack>
          )}
        </ConfirmModal>
      )}
    </Stack>
  );
}
